<template>
  <a
    class="product-card-simple"
    :class="{
      [$class('loading')]: !product,
      'show-secondary-image': showSecondaryImage,
    }"
    :href="product.url"
    @focus="loadSecondaryImage = true"
    @mouseenter="loadSecondaryImage = true"
  >
    <div class="product-card-simple__image-container">
      <responsive-image
        class="product-card-simple__primary-image"
        :image="product.featuredImage"
        min-max="200-500"
        ratio="1:1"
        sizes="(min-width: 1140px) 25vw, 50vw"
      />

      <responsive-image
        v-if="loadSecondaryImage && product.secondaryImage"
        class="product-card-simple__secondary-image"
        :image="product.secondaryImage"
        min-max="200-500"
        :padding="false"
        ratio="1:1"
        sizes="(min-width: 1140px) 25vw, 50vw"
        @image-loaded="showSecondaryImage = true"
      />
    </div>

    <div class="product-card-simple__footer">
      <product-vendor
        class="product-card-simple__vendor"
        :product="product"
      />

      <p
        class="
        product-card-simple__title
        text-heading-s
      "
        v-text="product.title"
      />

      <product-prices
        v-if="!hasSubscription"
        class="product-card-simple__prices"
        class-compare-at-price="text-other-sale-l"
        class-price="text-body-m-regular"
        :available="product?.available"
        :compare-at-price="product?.compareAtPrice?.amount"
        :price="product?.price?.amount"
        :varies="product?.price?.varies"
      />

      <div
        v-if="hasSubscription"
        class="product-card-simple__subscription"
      >
        <div class="product-card-simple__subscription-price">
          <span class="product-card-simple__subscription-from text-body-xs-regular">
            {{ fromText }} {{ storeCurrency }}
          </span>
          <span class="product-card-simple__subscription-amount text-body-s-bold">
            {{ $formatMoney(minimumPlanPrice) }}
          </span>
          <span class="product-card-simple__subscription-frequency text-body-xs-regular">
            / {{ minimumPlanPeriodText }}
          </span>
        </div>
        <div
          v-if="subscriptionCancelText"
          class="product-card-simple__subscription-cancel text-body-xs-regular"
          :style="{ '--subscription-cancel-color': subscriptionCancelColor }"
        >
          {{ subscriptionCancelText }}
        </div>
        <div
          class="product-card-simple__subscription-plans text-body-xs-regular"
          :style="subscriptionSettings.colors?.plans
            ? { color: subscriptionSettings.colors.plans }
            : {}"
        >
          {{ plansAvailableText }}
        </div>
        <div
          v-if="subscriptionSettings.lines?.additional"
          class="product-card-simple__subscription-additional text-body-xs-regular"
          :style="subscriptionSettings.colors?.additionalLine
            ? { color: subscriptionSettings.colors.additionalLine }
            : {}"
        >
          {{ subscriptionSettings.lines.additional }}
        </div>
      </div>
    </div>
  </a>
</template>

<script>

/**
 * Vue: Product card simple (product-card-simple)
 * -----------------------------------------------------------------------------
 * Product card simple used throughout the site.
 *
 * @param {Boolean|Object} product - Product object, false shows loading state.
 *
 */
import ProductPrices from '~global/product-prices/product-prices'
import ProductVendor from '~async/product-vendor/product-vendor'
import ResponsiveImage from '~global/images/responsive-image'

export default {
  name: 'ProductCardSimple',

  components: {
    ProductPrices,
    ProductVendor,
    ResponsiveImage,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    subscriptionSettings: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      loadSecondaryImage: false,
      showSecondaryImage: false,
    }
  },

  computed: {

    /**
     * Check if product has subscription selling plans.
     * @returns {Boolean}
     */
    hasSubscription() {
      return this.product?.sellingPlanGroups?.length > 0
    },

    /**
     * Find the minimum raw plan price and its billing period across all selling
     * plans. No monthly-rate normalisation — the plan's stated price is used
     * directly so the displayed amount always matches the plan total.
     * @returns {Object}
     */
    planData() {
      if (!this.hasSubscription) {
        return {
          minimumPrice: 0,
          minimumPlanInterval: 'month',
          minimumPlanIntervalCount: 1,
          totalPlans: 0,
        }
      }

      let minimumPrice = null
      let minimumPlanInterval = 'month'
      let minimumPlanIntervalCount = 1
      let totalPlans = 0

      this.product.sellingPlanGroups.forEach((group) => {
        group.sellingPlans?.forEach((plan) => {
          totalPlans += 1
          const price = plan.priceAdjustments?.[0]?.value || 0

          if (minimumPrice === null || price < minimumPrice) {
            minimumPrice = price

            /** Parse interval and count from plan name
             * (e.g. "1 Month", "3 Years", "6 Months")
             */
            const nameLower = (plan.name || '').toLowerCase()
            const numberMatch = nameLower.match(/(\d+)\s*(year|month)/)

            if (numberMatch) {
              minimumPlanIntervalCount = parseInt(numberMatch[1])
              minimumPlanInterval = numberMatch[2]
            } else if (nameLower.includes('year')) {
              minimumPlanInterval = 'year'
              minimumPlanIntervalCount = 1
            } else {
              minimumPlanInterval = 'month'
              minimumPlanIntervalCount = 1
            }
          }
        })
      })

      return {
        minimumPrice: minimumPrice ?? 0,
        minimumPlanInterval,
        minimumPlanIntervalCount,
        totalPlans,
      }
    },

    /**
     * Get the minimum plan price (raw, no division).
     * @returns {Number}
     */
    minimumPlanPrice() {
      return this.planData.minimumPrice
    },

    /**
     * Get the period label for the minimum plan.
     * - count=1, interval='month' → "month"
     * - count=1, interval='year'  → "year"
     * - count=3, interval='year'  → "3 years"
     * - count=6, interval='month' → "6 months"
     * @returns {String}
     */
    minimumPlanPeriodText() {
      const interval = this.planData.minimumPlanInterval
      const count = this.planData.minimumPlanIntervalCount

      if (interval === 'year') {
        if (count === 1) {
          return this.subscriptionSettings.text?.perYear || 'year'
        }
        return `${count} years`
      }

      if (count === 1) {
        return this.subscriptionSettings.text?.perMonth || 'month'
      }
      return `${count} months`
    },

    /**
     * Get the "From" prefix text.
     * @returns {String}
     */
    fromText() {
      return this.subscriptionSettings.text?.from || 'From'
    },

    /**
     * Get the store currency ISO code (e.g. "USD").
     * @returns {String}
     */
    storeCurrency() {
      return cnvs?.store?.currency || ''
    },

    /**
     * Get total number of selling plans.
     * @returns {Number}
     */
    totalSellingPlans() {
      return this.planData.totalPlans
    },

    /**
     * Get formatted plans available text.
     * @returns {String}
     */
    plansAvailableText() {
      const singular = this.subscriptionSettings.text?.plansAvailableSingular ||
        '__COUNT__ subscription plan available'
      const plural = this.subscriptionSettings.text?.plansAvailablePlural ||
        '__COUNT__ subscription plans available'
      const template = this.totalSellingPlans === 1 ? singular : plural
      return template.replace('__COUNT__', this.totalSellingPlans)
    },

    /**
     * Get cancel line text.
     * @returns {String}
     */
    subscriptionCancelText() {
      return this.$variable('settings.subscriptionCancelText') || ''
    },

    /**
     * Get cancel line color.
     * @returns {String}
     */
    subscriptionCancelColor() {
      return this.$variable('settings.subscriptionCancelColor') || '#d32f2f'
    },
  },
}
</script>

<style lang="scss">
@import './product-card-simple';
</style>
